import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Plus, MoreVertical, Send, Edit, Trash2, Eye, Download, Search, XCircle, Upload, FileText, CheckCircle, Loader2, Users } from "lucide-react";
import { toast } from "sonner";

const statusLabels = { draft: "Brouillon", sent: "Envoyé", envoye_simple: "Envoyé (PDF)", accepted: "Accepté", rejected: "Refusé", annule: "Annulé" };
const statusColors = {
  draft: "bg-slate-100 text-slate-600", sent: "bg-blue-50 text-blue-600",
  envoye_simple: "bg-violet-50 text-violet-600",
  accepted: "bg-emerald-50 text-emerald-600", rejected: "bg-red-50 text-red-600",
  annule: "bg-amber-50 text-amber-600"
};

export default function DevisPage() {
  const { apiFetch, API, user } = useAuth();
  const isWebmaster = user?.role === "webmaster";
  const navigate = useNavigate();
  const [devisList, setDevisList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Import state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importStep, setImportStep] = useState("upload"); // upload | analyzing | preview | confirming
  const [importFile, setImportFile] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [sendPromptDevisId, setSendPromptDevisId] = useState(null);
  const [sendMode, setSendMode] = useState(null); // null | "resend" | "other" | "pdf" | "pdf_other"
  const [altRecipient, setAltRecipient] = useState("");
  const [altRecipientName, setAltRecipientName] = useState("");
  const [clientsList, setClientsList] = useState([]);
  const fileInputRef = useRef(null);

  // Fetch clients for the recipient picker
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await apiFetch(`${API}/clients`);
        if (res.ok) setClientsList(await res.json());
      } catch {}
    };
    fetchClients();
  }, [apiFetch, API]);

  const fetchDevis = useCallback(async () => {
    try {
      const url = filter === "all" ? `${API}/devis` : `${API}/devis?status=${filter}`;
      const res = await apiFetch(url);
      if (res.ok) setDevisList(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [apiFetch, API, filter]);

  useEffect(() => { fetchDevis(); }, [fetchDevis]);

  const handleDelete = async (devisId) => {
    if (!window.confirm("ATTENTION : Supprimer ce devis supprimera aussi le Bon de Commande, la Facture et l'Intervention liés.\n\nConfirmez-vous la suppression ?")) return;
    const res = await apiFetch(`${API}/devis/${devisId}`, { method: "DELETE" });
    if (res.ok) {
      const data = await res.json();
      toast.success(data.message || "Devis et documents liés supprimés");
      fetchDevis();
    }
  };

  const handleCancel = async (devisId) => {
    if (!window.confirm("Annuler ce devis annulera aussi le BC, la facture et l'intervention liés.\n\nConfirmez-vous l'annulation ?")) return;
    const res = await apiFetch(`${API}/devis/${devisId}/cancel`, { method: "PUT" });
    if (res.ok) { toast.success("Devis et documents liés annulés"); fetchDevis(); }
  };

  const handleSend = async (devisId) => {
    const d = devisList.find(dv => dv.devis_id === devisId);
    if (!d) return;
    const hasCc = d?.cc_recipients?.filter(cc => cc.email)?.length > 0;
    const alreadySent = d.status === "sent" || d.status === "accepted" || d.status === "rejected";
    if (alreadySent || !hasCc) {
      // Show the send dialog
      setSendPromptDevisId(devisId);
      setSendMode(null);
      setAltRecipient("");
      setAltRecipientName("");
      return;
    }
    // First send with CCi — send directly
    const res = await apiFetch(`${API}/devis/${devisId}/send`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) });
    if (res.ok) { const data = await res.json(); toast.success(data.message); fetchDevis(); }
    else { const err = await res.json(); toast.error(err.detail || "Erreur"); }
  };

  const executeSend = async () => {
    const devisId = sendPromptDevisId;
    if (!devisId) return;
    let body = {};
    let endpoint = `${API}/devis/${devisId}/send`;

    if (sendMode === "resend") {
      body = {};
    } else if (sendMode === "other") {
      if (!altRecipient) { toast.error("Saisissez un email"); return; }
      body = { send_to: altRecipient, send_to_name: altRecipientName, skip_main: true, skip_cc: true };
    } else if (sendMode === "pdf") {
      endpoint = `${API}/devis/${devisId}/send-pdf`;
      body = {};
    } else if (sendMode === "pdf_other") {
      if (!altRecipient) { toast.error("Saisissez un email"); return; }
      endpoint = `${API}/devis/${devisId}/send-pdf`;
      body = { send_to: altRecipient, skip_main: true };
    } else {
      return;
    }
    setSendPromptDevisId(null);
    const res = await apiFetch(endpoint, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    if (res.ok) { const data = await res.json(); toast.success(data.message); fetchDevis(); }
    else { const err = await res.json(); toast.error(err.detail || "Erreur"); }
  };

  const handleSendForce = async (devisId) => {
    setSendPromptDevisId(null);
    const res = await apiFetch(`${API}/devis/${devisId}/send`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) });
    if (res.ok) { const data = await res.json(); toast.success(data.message); fetchDevis(); }
    else { const err = await res.json(); toast.error(err.detail || "Erreur"); }
  };

  const handleAccept = async (devisId) => {
    if (!window.confirm("Marquer ce devis comme accepté ?\nLe BC et l'intervention seront créés automatiquement.")) return;
    const res = await apiFetch(`${API}/devis/${devisId}/accept`, { method: "POST" });
    if (res.ok) {
      const data = await res.json();
      const bcNum = data.commande_number || "";
      toast.success(`Devis accepté${bcNum ? ` — BC ${bcNum} et intervention créés` : ""}`);
      fetchDevis();
    }
    else { const err = await res.json(); toast.error(err.detail || "Erreur"); }
  };

  const handleDownloadPdf = async (devisId, devisNumber) => {
    try {
      const res = await apiFetch(`${API}/devis/${devisId}/pdf`);
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${devisNumber.replace(/\//g, "-")}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) { toast.error("Erreur téléchargement"); }
  };

  // --- Import functions ---
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type === "application/pdf") {
      setImportFile(file);
    } else {
      toast.error("Veuillez sélectionner un fichier PDF");
    }
  };

  const handleAnalyze = async () => {
    if (!importFile) return;
    setImportStep("analyzing");
    try {
      const formData = new FormData();
      formData.append("file", importFile);
      const token = localStorage.getItem("jvc_token");
      const res = await fetch(`${API}/devis/import`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        setExtractedData(data.extracted);
        setImportStep("preview");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur d'analyse");
        setImportStep("upload");
      }
    } catch (e) {
      toast.error("Erreur lors de l'analyse du PDF");
      setImportStep("upload");
    }
  };

  const handleConfirmImport = async () => {
    setImportStep("confirming");
    try {
      const res = await apiFetch(`${API}/devis/import/confirm`, {
        method: "POST",
        body: JSON.stringify({ extracted: extractedData, document_type: "devis" }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success("Devis importé en brouillon — Acceptez-le pour créer le BC et l'intervention");
        setImportDialogOpen(false);
        setImportStep("upload");
        setImportFile(null);
        setExtractedData(null);
        fetchDevis();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur");
        setImportStep("preview");
      }
    } catch (e) {
      toast.error("Erreur d'import");
      setImportStep("preview");
    }
  };

  const resetImport = () => {
    setImportStep("upload");
    setImportFile(null);
    setExtractedData(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  };

  const filtered = devisList.filter(d => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return d.devis_number?.toLowerCase().includes(q) ||
           d.client_name?.toLowerCase().includes(q) ||
           d.objet?.toLowerCase().includes(q);
  });

  return (
    <div className="p-4 sm:p-8" data-testid="devis-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
        <div>
          <h1 className="font-barlow text-xl sm:text-2xl font-bold text-slate-900 tracking-tight uppercase">Devis</h1>
          <p className="text-sm text-slate-500 mt-1">Gérez vos devis et envoyez-les à vos clients</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { resetImport(); setImportDialogOpen(true); }} className="gap-2" data-testid="import-devis-btn">
            <Upload className="w-4 h-4" /> Importer PDF
          </Button>
          <Button onClick={() => navigate("/devis/new")} className="bg-orange-500 hover:bg-orange-600 text-white gap-2" data-testid="create-devis-btn">
            <Plus className="w-4 h-4" /> Nouveau Devis
          </Button>
        </div>
      </div>

      <Tabs value={filter} onValueChange={setFilter} className="mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList className="w-max">
              <TabsTrigger value="all" data-testid="filter-all">Tous</TabsTrigger>
              <TabsTrigger value="draft" data-testid="filter-draft">Brouillons</TabsTrigger>
              <TabsTrigger value="sent" data-testid="filter-sent">Envoyés</TabsTrigger>
              <TabsTrigger value="accepted" data-testid="filter-accepted">Acceptés</TabsTrigger>
              <TabsTrigger value="rejected" data-testid="filter-rejected">Refusés</TabsTrigger>
              <TabsTrigger value="annule" data-testid="filter-annule">Annulés</TabsTrigger>
            </TabsList>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Rechercher..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9 h-9" data-testid="devis-search" />
          </div>
        </div>
      </Tabs>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-400">Chargement...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-slate-400 mb-4">Aucun devis trouvé</p>
              <Button onClick={() => navigate("/devis/new")} variant="outline" data-testid="empty-create-btn">Créer un devis</Button>
            </div>
          ) : (
            <div className="overflow-x-auto"><Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs font-semibold">N° Devis</TableHead>
                  <TableHead className="text-xs font-semibold hidden sm:table-cell">Client</TableHead>
                  <TableHead className="text-xs font-semibold hidden md:table-cell">Date</TableHead>
                  <TableHead className="text-xs font-semibold text-right">Montant</TableHead>
                  <TableHead className="text-xs font-semibold">Statut</TableHead>
                  <TableHead className="text-xs font-semibold w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(d => (
                  <TableRow key={d.devis_id} data-testid={`devis-row-${d.devis_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-900">{d.devis_number}</span>
                        {d.source === "import" && (
                          <Badge className="bg-violet-50 text-violet-600 text-[9px] border-0 px-1.5">Importé</Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 sm:hidden">{d.client_name}</p>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600 hidden sm:table-cell">{d.client_name}</TableCell>
                    <TableCell className="text-sm text-slate-500 hidden md:table-cell">{formatDate(d.created_at)}</TableCell>
                    <TableCell className="text-sm text-right font-semibold text-slate-900">{d.total?.toLocaleString("fr-FR")} EUR</TableCell>
                    <TableCell>
                      <Badge className={`${statusColors[d.status]} text-[11px] font-medium border-0`}>{statusLabels[d.status]}</Badge>
                      {d.status === "accepted" && d.accepted_by_name && (
                        <p className="text-[10px] text-emerald-600 mt-0.5 truncate max-w-[140px]" title={`Accepté par ${d.accepted_by_name} (${d.accepted_by_email || ""})`}>
                          par {d.accepted_by_name}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`devis-menu-${d.devis_id}`}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/devis/${d.devis_id}/edit`)} data-testid={`devis-edit-${d.devis_id}`}>
                            <Edit className="w-4 h-4 mr-2" /> Modifier
                          </DropdownMenuItem>
                          {d.status !== "annule" && (
                            <DropdownMenuItem onClick={() => handleSend(d.devis_id)} data-testid={`devis-send-${d.devis_id}`}>
                              <Send className="w-4 h-4 mr-2" /> Envoyer
                            </DropdownMenuItem>
                          )}
                          {(d.status === "draft" || d.status === "sent" || d.status === "envoye_simple") && (
                            <DropdownMenuItem onClick={() => handleAccept(d.devis_id)} data-testid={`devis-accept-${d.devis_id}`}>
                              <CheckCircle className="w-4 h-4 mr-2 text-emerald-600" /> Accepter
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => window.open(`/portal/${d.access_token}`, '_blank')} data-testid={`devis-preview-${d.devis_id}`}>
                            <Eye className="w-4 h-4 mr-2" /> Aperçu portail
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDownloadPdf(d.devis_id, d.devis_number)} data-testid={`devis-pdf-${d.devis_id}`}>
                            <Download className="w-4 h-4 mr-2" /> Télécharger PDF
                          </DropdownMenuItem>
                          {isWebmaster && (
                            <DropdownMenuItem onClick={() => handleDelete(d.devis_id)} className="text-red-600" data-testid={`devis-delete-${d.devis_id}`}>
                              <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                            </DropdownMenuItem>
                          )}
                          {d.status !== "annule" && (
                            <DropdownMenuItem onClick={() => handleCancel(d.devis_id)} className="text-amber-600" data-testid={`devis-cancel-${d.devis_id}`}>
                              <XCircle className="w-4 h-4 mr-2" /> Annuler
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></div>
          )}
        </CardContent>
      </Card>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={v => { if (!v) resetImport(); setImportDialogOpen(v); }}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-barlow text-lg uppercase tracking-tight flex items-center gap-2">
              <Upload className="w-5 h-5" /> Importer un devis PDF
            </DialogTitle>
            <DialogDescription>
              Uploadez un ancien devis PDF — l'IA analysera le contenu. Le devis sera importé en brouillon. Acceptez-le ensuite pour créer le BC et l'intervention.
            </DialogDescription>
          </DialogHeader>

          {importStep === "upload" && (
            <div className="space-y-4">
              <div
                className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-slate-400 transition-colors"
                onClick={() => fileInputRef.current?.click()}
                data-testid="import-drop-zone"
              >
                <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm font-medium text-slate-600">{importFile ? importFile.name : "Cliquez pour sélectionner un PDF"}</p>
                <p className="text-xs text-slate-400 mt-1">Le devis sera analysé par IA pour extraire les données</p>
                <input ref={fileInputRef} type="file" accept=".pdf" className="hidden" onChange={handleFileSelect} data-testid="import-file-input" />
              </div>
              {importFile && (
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-md">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-800">{importFile.name}</span>
                  </div>
                  <Button onClick={handleAnalyze} className="bg-blue-600 hover:bg-blue-700 gap-2" data-testid="analyze-pdf-btn">
                    <Search className="w-4 h-4" /> Analyser
                  </Button>
                </div>
              )}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800 space-y-1">
                <p className="font-semibold">Important :</p>
                <p>- La référence et la date du devis original seront conservées</p>
                <p>- Aucun email ne sera envoyé au client</p>
                <p>- Le client sera créé automatiquement s'il n'existe pas</p>
                <p className="font-semibold text-blue-700">- Le devis sera importé en brouillon. Acceptez-le pour déclencher la création du BC et de l'intervention.</p>
              </div>
            </div>
          )}

          {importStep === "analyzing" && (
            <div className="py-12 text-center space-y-4">
              <Loader2 className="w-10 h-10 text-blue-500 animate-spin mx-auto" />
              <div>
                <p className="text-sm font-medium text-slate-700">Analyse du PDF en cours...</p>
                <p className="text-xs text-slate-400 mt-1">L'IA extrait les informations du devis</p>
              </div>
            </div>
          )}

          {importStep === "preview" && extractedData && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-md">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <p className="text-sm font-medium text-emerald-800">Données extraites avec succès</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-slate-50 rounded-md">
                  <p className="text-[10px] text-slate-400 uppercase font-semibold mb-1">Référence</p>
                  <Input value={extractedData.reference || ""} onChange={e => setExtractedData(d => ({ ...d, reference: e.target.value }))} className="h-8 text-sm font-bold" data-testid="import-ref-input" />
                </div>
                <div className="p-3 bg-slate-50 rounded-md">
                  <p className="text-[10px] text-slate-400 uppercase font-semibold mb-1">Date</p>
                  <Input type="date" value={extractedData.date || ""} onChange={e => setExtractedData(d => ({ ...d, date: e.target.value }))} className="h-8 text-sm" data-testid="import-date-input" />
                </div>
              </div>

              <div className="p-3 bg-slate-50 rounded-md space-y-2">
                <p className="text-[10px] text-slate-400 uppercase font-semibold">Client</p>
                <Input value={extractedData.client?.company_name || ""} onChange={e => setExtractedData(d => ({ ...d, client: { ...d.client, company_name: e.target.value } }))} placeholder="Nom entreprise" className="h-8 text-sm font-semibold" data-testid="import-client-name" />
                <div className="grid grid-cols-2 gap-2">
                  <Input value={extractedData.client?.contact_name || ""} onChange={e => setExtractedData(d => ({ ...d, client: { ...d.client, contact_name: e.target.value } }))} placeholder="Contact" className="h-7 text-xs" data-testid="import-client-contact" />
                  <Input value={extractedData.client?.email || ""} onChange={e => setExtractedData(d => ({ ...d, client: { ...d.client, email: e.target.value } }))} placeholder="Email" className="h-7 text-xs" data-testid="import-client-email" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input value={extractedData.client?.address || ""} onChange={e => setExtractedData(d => ({ ...d, client: { ...d.client, address: e.target.value } }))} placeholder="Adresse" className="h-7 text-xs" data-testid="import-client-address" />
                  <Input value={extractedData.client?.city || ""} onChange={e => setExtractedData(d => ({ ...d, client: { ...d.client, city: e.target.value } }))} placeholder="Ville" className="h-7 text-xs" data-testid="import-client-city" />
                </div>
              </div>

              <div className="p-3 bg-blue-50 rounded-md">
                <p className="text-[10px] text-blue-400 uppercase font-semibold mb-1">Objet</p>
                <Input value={extractedData.objet || ""} onChange={e => setExtractedData(d => ({ ...d, objet: e.target.value }))} placeholder="Objet du devis" className="h-8 text-sm bg-white" data-testid="import-objet" />
              </div>

              {/* Items preview */}
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Lignes ({extractedData.items?.length || 0})</p>
                <div className="border rounded-md overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-slate-800 text-white">
                        <th className="p-2 text-left">Description</th>
                        <th className="p-2 text-center">Qté</th>
                        <th className="p-2 text-right">P.U.</th>
                        <th className="p-2 text-right">Total</th>
                      </tr></thead>
                      <tbody>
                        {extractedData.items?.map((item, idx) => (
                          item.item_type === "title" ? (
                            <tr key={idx} className="bg-slate-700 text-white font-bold">
                              <td colSpan={4} className="p-2 uppercase">{item.description}</td>
                            </tr>
                          ) : (
                            <tr key={idx} className={idx % 2 ? "bg-slate-50" : ""}>
                              <td className="p-2">{item.description}</td>
                              <td className="p-2 text-center">{item.quantity} {item.unit}</td>
                              <td className="p-2 text-right">{item.unit_price?.toFixed(2)}</td>
                              <td className="p-2 text-right font-medium">{(item.quantity * item.unit_price).toFixed(2)}</td>
                            </tr>
                          )
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-900 text-white rounded-md">
                <span className="font-semibold text-sm">TOTAL</span>
                <span className="text-lg font-bold font-barlow">{extractedData.total?.toFixed(2)} EUR</span>
              </div>

              <div className="flex justify-between gap-2 pt-2">
                <Button variant="outline" onClick={resetImport}>Recommencer</Button>
                <Button onClick={handleConfirmImport} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="confirm-import-btn">
                  <CheckCircle className="w-4 h-4" /> Confirmer l'import
                </Button>
              </div>
            </div>
          )}

          {importStep === "confirming" && (
            <div className="py-12 text-center space-y-4">
              <Loader2 className="w-10 h-10 text-emerald-500 animate-spin mx-auto" />
              <p className="text-sm font-medium text-slate-700">Création du devis, BC et intervention...</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Dialog - smart send options */}
      <Dialog open={!!sendPromptDevisId} onOpenChange={v => { if (!v) { setSendPromptDevisId(null); setSendMode(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-barlow text-base uppercase tracking-tight flex items-center gap-2">
              <Send className="w-4 h-4" /> Options d'envoi
            </DialogTitle>
            <DialogDescription>
              {(() => {
                const d = devisList.find(dv => dv.devis_id === sendPromptDevisId);
                const alreadySent = d && (d.status === "sent" || d.status === "accepted" || d.status === "rejected");
                const hasCc = d?.cc_recipients?.filter(cc => cc.email)?.length > 0;
                if (alreadySent) return `Ce devis a déjà été envoyé à ${d.client_email}. Que souhaitez-vous faire ?`;
                if (!hasCc) return "Aucun CCi ajouté. Que souhaitez-vous faire ?";
                return "Comment souhaitez-vous envoyer ce devis ?";
              })()}
            </DialogDescription>
          </DialogHeader>

          {!sendMode && (
            <div className="space-y-2">
              {(() => {
                const d = devisList.find(dv => dv.devis_id === sendPromptDevisId);
                const alreadySent = d && (d.status === "sent" || d.status === "accepted" || d.status === "rejected");
                return (
                  <>
                    <button onClick={() => { setSendMode("resend"); }} className="w-full text-left p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors" data-testid="send-opt-resend">
                      <p className="text-sm font-medium text-slate-800">Renvoyer au destinataire principal {alreadySent ? "(à nouveau)" : ""}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{d?.client_email} + CCi existants</p>
                    </button>
                    <button onClick={() => setSendMode("other")} className="w-full text-left p-3 rounded-lg border border-slate-200 hover:bg-blue-50 transition-colors" data-testid="send-opt-other">
                      <p className="text-sm font-medium text-slate-800">Envoyer à un autre destinataire</p>
                      <p className="text-xs text-slate-400 mt-0.5">Sans renvoyer au destinataire principal ni aux CCi</p>
                    </button>
                    <button onClick={() => setSendMode("pdf")} className="w-full text-left p-3 rounded-lg border border-violet-200 hover:bg-violet-50 transition-colors" data-testid="send-opt-pdf">
                      <p className="text-sm font-medium text-violet-700">Envoyer en PDF simple (sans validation)</p>
                      <p className="text-xs text-slate-400 mt-0.5">PDF en pièce jointe, pas de portail — vous validez manuellement après</p>
                    </button>
                    <button onClick={() => { setSendPromptDevisId(null); navigate(`/devis/${sendPromptDevisId}`); }} className="w-full text-left p-3 rounded-lg border border-slate-200 hover:bg-amber-50 transition-colors" data-testid="send-opt-edit">
                      <p className="text-sm font-medium text-slate-800">Modifier le devis</p>
                      <p className="text-xs text-slate-400 mt-0.5">Ajouter des CCi ou modifier le contenu avant d'envoyer</p>
                    </button>
                  </>
                );
              })()}
            </div>
          )}

          {sendMode === "resend" && (
            <DialogFooter className="flex gap-2 sm:gap-2">
              <Button variant="outline" onClick={() => setSendMode(null)}>Retour</Button>
              <Button onClick={executeSend} className="bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="confirm-resend-btn">
                <Send className="w-3.5 h-3.5" /> Confirmer l'envoi
              </Button>
            </DialogFooter>
          )}

          {sendMode === "pdf" && (
            <div className="space-y-3">
              <div className="p-3 bg-violet-50 border border-violet-100 rounded-lg">
                <p className="text-sm text-violet-700">Le devis sera envoyé en <strong>PDF en pièce jointe</strong> au destinataire principal{(() => { const d = devisList.find(dv => dv.devis_id === sendPromptDevisId); return d?.cc_recipients?.filter(cc => cc.email)?.length > 0 ? " + CCi" : ""; })()}.</p>
                <p className="text-xs text-violet-500 mt-1">Pas de portail de validation — vous acceptez manuellement quand le client confirme.</p>
              </div>
              <button onClick={() => setSendMode("pdf_other")} className="w-full text-left p-2.5 rounded-lg border border-slate-200 hover:bg-blue-50 transition-colors text-xs">
                <span className="text-slate-600">Ou envoyer le PDF à un autre destinataire...</span>
              </button>
              <DialogFooter className="flex gap-2 sm:gap-2">
                <Button variant="outline" onClick={() => setSendMode(null)}>Retour</Button>
                <Button onClick={executeSend} className="bg-violet-700 hover:bg-violet-800 gap-1.5" data-testid="confirm-send-pdf-btn">
                  <FileText className="w-3.5 h-3.5" /> Envoyer le PDF
                </Button>
              </DialogFooter>
            </div>
          )}

          {sendMode === "pdf_other" && (
            <div className="space-y-3">
              <div className="p-3 bg-violet-50 border border-violet-100 rounded-lg">
                <p className="text-xs text-violet-600">PDF en pièce jointe, envoyé uniquement à l'autre destinataire (pas au principal)</p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-slate-600">Choisir un contact existant :</Label>
                <select className="w-full h-9 text-sm rounded-md border border-slate-200 px-3 bg-white" value="" onChange={e => { const val = e.target.value; if (!val) return; const [email, name] = val.split("|||"); setAltRecipient(email); setAltRecipientName(name || ""); }} data-testid="pdf-alt-recipient-select">
                  <option value="">— Sélectionner un contact —</option>
                  {clientsList.map(c => {
                    const contacts = [
                      { email: c.email, name: c.contact_name || c.company_name, label: `${c.company_name} — ${c.contact_name || c.email}` },
                      ...(c.contacts || []).map(ct => ({ email: ct.email, name: ct.name, label: `${c.company_name} — ${ct.name}${ct.position ? ` (${ct.position})` : ""}` }))
                    ];
                    return contacts.map((ct, i) => (
                      <option key={`${c.client_id}-${i}`} value={`${ct.email}|||${ct.name}`}>{ct.label} — {ct.email}</option>
                    ));
                  })}
                </select>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400"><Separator className="flex-1" /> ou saisir <Separator className="flex-1" /></div>
              <Input placeholder="Email du destinataire" type="email" value={altRecipient} onChange={e => setAltRecipient(e.target.value)} className="h-9 text-sm" data-testid="pdf-alt-email" />
              <DialogFooter className="flex gap-2 sm:gap-2 pt-1">
                <Button variant="outline" onClick={() => setSendMode("pdf")}>Retour</Button>
                <Button onClick={executeSend} disabled={!altRecipient} className="bg-violet-700 hover:bg-violet-800 gap-1.5" data-testid="confirm-send-pdf-other-btn">
                  <FileText className="w-3.5 h-3.5" /> Envoyer le PDF à {altRecipient || "..."}
                </Button>
              </DialogFooter>
            </div>
          )}

          {sendMode === "other" && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label className="text-xs text-slate-600">Choisir un contact existant :</Label>
                <select
                  className="w-full h-9 text-sm rounded-md border border-slate-200 px-3 bg-white"
                  value=""
                  onChange={e => {
                    const val = e.target.value;
                    if (!val) return;
                    const [email, name] = val.split("|||");
                    setAltRecipient(email);
                    setAltRecipientName(name || "");
                  }}
                  data-testid="alt-recipient-select"
                >
                  <option value="">— Sélectionner un contact —</option>
                  {clientsList.map(c => {
                    const contacts = [
                      { email: c.email, name: c.contact_name || c.company_name, label: `${c.company_name} — ${c.contact_name || c.email}` },
                      ...(c.contacts || []).map(ct => ({ email: ct.email, name: ct.name, label: `${c.company_name} — ${ct.name}${ct.position ? ` (${ct.position})` : ""}` }))
                    ];
                    return contacts.map((ct, i) => (
                      <option key={`${c.client_id}-${i}`} value={`${ct.email}|||${ct.name}`}>{ct.label} — {ct.email}</option>
                    ));
                  })}
                </select>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400"><Separator className="flex-1" /> ou saisir manuellement <Separator className="flex-1" /></div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Email du destinataire" type="email" value={altRecipient} onChange={e => setAltRecipient(e.target.value)} className="h-9 text-sm" data-testid="alt-recipient-email" />
                <Input placeholder="Nom (optionnel)" value={altRecipientName} onChange={e => setAltRecipientName(e.target.value)} className="h-9 text-sm" data-testid="alt-recipient-name" />
              </div>
              <DialogFooter className="flex gap-2 sm:gap-2 pt-1">
                <Button variant="outline" onClick={() => setSendMode(null)}>Retour</Button>
                <Button onClick={executeSend} disabled={!altRecipient} className="bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="confirm-send-other-btn">
                  <Send className="w-3.5 h-3.5" /> Envoyer à {altRecipient || "..."}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
